import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import * as api from '../../api';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { FD100MultiSelectOptions } from '../../Plateforme/Utils/Listings';
import { useTranslation } from 'react-i18next';
import { makeUrlsAndEmailsLinks } from '../../Plateforme/Utils/Format';

dayjs.locale('fr');

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const PopupContainer = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 1000px;
  max-height: 90vh;
  width: 100%;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: sticky;
  top: 15px;
  float: right;
  margin: 15px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #c82333;
  }
`;

const HeaderSection = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 12px 12px 0 0;
  border-bottom: 2px solid #e0e0e0;
`;

const Title = styled.h2`
  color: #333;
  margin: 0 0 20px 0;
  font-size: 1.8rem;
`;

const MetaInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const MetaItem = styled.div``;

const MetaLabel = styled.span`
  color: #666;
  font-size: 13px;
  display: block;
  margin-bottom: 5px;
`;

const MetaValue = styled.span`
  color: #333;
  font-size: 15px;
  font-weight: 500;
`;

const ContentSection = styled.div`
  padding: 25px 30px;
`;

const Section = styled.div`
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const SectionTitle = styled.h3`
  color: #0000FF;
  margin-bottom: 20px;
  font-size: 1.3rem;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 15px;
  margin-bottom: 15px;
`;

const DataLabel = styled.div`
  font-weight: 600;
  color: #0000FF;
  font-size: 14px;
`;

const DataValue = styled.div`
  color: #333;
  white-space: pre-wrap;
  font-size: 14px;
`;

const PieceCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
`;

const PieceTitle = styled.h4`
  color: #333;
  margin-bottom: 15px;
  font-size: 1.1rem;
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 20px;
  margin-top: 15px;
`;

const MediaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MediaThumb = styled.div`
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  height: 120px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MediaInfo = styled.div`
  font-size: 11px;
  color: #666;
  text-align: center;
`;

const MediaName = styled.div`
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MediaCredit = styled.div`
  font-style: italic;
  color: #888;
`;

const MediaIcon = styled.div`
  font-size: 36px;
  color: #adb5bd;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  
  ${props => {
    switch(props.status) {
      case 'pending': return 'background: #ffc107; color: #000;';
      case 'reviewed': return 'background: #17a2b8; color: white;';
      case 'approved': return 'background: #28a745; color: white;';
      case 'rejected': return 'background: #dc3545; color: white;';
      default: return 'background: #6c757d; color: white;';
    }
  }}
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const CandidatureCompletePopup = ({ participationId, onClose }) => {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { i18n } = useTranslation();

  // Get typologie options in the current language
  const tyoplogieEspaceFD100 = FD100MultiSelectOptions.tyoplogieEspaceFD100(i18n.language);
  const typologieObjetFD100 = FD100MultiSelectOptions.typologieObjetFD100(i18n.language);
  const FDCategories = [
    ...tyoplogieEspaceFD100,
    ...typologieObjetFD100,
  ].filter((cat, index, self) => self.findIndex((t) => t.value === cat.value) === index);

  useEffect(() => {
    fetchSubmission();
  }, [participationId]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      const response = await api.getFormSubmissionById(participationId);
      setSubmission(response.data);
    } catch (err) {
      console.error('Error fetching submission:', err);
      setError('Erreur lors du chargement de la candidature');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Nouveau';
      case 'reviewed': return 'Examiné';
      case 'approved': return 'Approuvé';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  const handleMediaClick = (mediaUrl) => {
    window.open(mediaUrl, '_blank');
  };

  if (loading) {
    return (
      <PopupOverlay onClick={handleOverlayClick}>
        <PopupContainer>
          <CloseButton onClick={onClose}>×</CloseButton>
          <ContentSection>
            <LoadingSpinner>Chargement de la candidature...</LoadingSpinner>
          </ContentSection>
        </PopupContainer>
      </PopupOverlay>
    );
  }

  if (error || !submission) {
    return (
      <PopupOverlay onClick={handleOverlayClick}>
        <PopupContainer>
          <CloseButton onClick={onClose}>×</CloseButton>
          <ContentSection>
            <p style={{ color: 'red', textAlign: 'center' }}>
              {error || 'Candidature non trouvée'}
            </p>
          </ContentSection>
        </PopupContainer>
      </PopupOverlay>
    );
  }

  const { data } = submission;
  const mediaBaseUrl = process.env.REACT_APP_MEDIA_BASE_URL;

  return (
    <PopupOverlay onClick={handleOverlayClick}>
      <PopupContainer>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <HeaderSection>
          <Title>
            Candidature Milan 2026 - {data.userType === 'designer' ? data.designerCompanyName : data.fabricantCompanyName}
          </Title>
          <MetaInfo>
            <MetaItem>
              <MetaLabel>Date de soumission</MetaLabel>
              <MetaValue>{dayjs(submission.createdAt).format('DD MMMM YYYY à HH:mm')}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>ID de soumission</MetaLabel>
              <MetaValue>{submission._id}</MetaValue>
            </MetaItem>
          </MetaInfo>
        </HeaderSection>

        <ContentSection>
          <Section>
            <SectionTitle>Contact du candidat</SectionTitle>
            <DataGrid>
              <DataLabel>Prénom:</DataLabel>
              <DataValue>{makeUrlsAndEmailsLinks(data.firstName)}</DataValue>

              <DataLabel>Nom:</DataLabel>
              <DataValue>{makeUrlsAndEmailsLinks(data.lastName)}</DataValue>

              <DataLabel>Email:</DataLabel>
              <DataValue>{makeUrlsAndEmailsLinks(data.email)}</DataValue>

              <DataLabel>Téléphone:</DataLabel>
              <DataValue>{makeUrlsAndEmailsLinks(data.phoneNumber)}</DataValue>

              <DataLabel>Type d'utilisateur:</DataLabel>
              <DataValue>{data.userType === 'designer' ? 'Designer' : 'Fabricant/Éditeur'}</DataValue>
            </DataGrid>
          </Section>

          {data.userType === 'designer' ? (
            <Section>
              <SectionTitle>Informations du designer</SectionTitle>
              <DataGrid>
                <DataLabel>Nom de l'entreprise:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(data.designerCompanyName)}</DataValue>

                <DataLabel>Présence internationale:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(data.designerInternationalPresence)}</DataValue>

                {data.designerInternationalDetails && (
                  <>
                    <DataLabel>Détails international:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(data.designerInternationalDetails)}</DataValue>
                  </>
                )}

                <DataLabel>Site web et réseaux sociaux:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(data.designerWebAndSocial)}</DataValue>

                {data.designerWebAndSocialDetails && (
                  <>
                    <DataLabel>Détails web/social:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(data.designerWebAndSocialDetails)}</DataValue>
                  </>
                )}

                {data.designerPresentation && (
                  <>
                    <DataLabel>Présentation:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(data.designerPresentation)}</DataValue>
                  </>
                )}
              </DataGrid>
            </Section>
          ) : (
            <Section>
              <SectionTitle>Informations du fabricant/éditeur</SectionTitle>
              <DataGrid>
                <DataLabel>Nom de l'entreprise:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(data.fabricantCompanyName)}</DataValue>

                <DataLabel>Cotisant CODIFAB:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(data.fabricantCodifab)}</DataValue>

                <DataLabel>Adhérent Ameublement français:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(data.fabricantAmeublement)}</DataValue>

                <DataLabel>Présence internationale:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(data.fabricantInternationalPresence)}</DataValue>

                {data.fabricantInternationalDetails && (
                  <>
                    <DataLabel>Détails international:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(data.fabricantInternationalDetails)}</DataValue>
                  </>
                )}

                <DataLabel>Site web et réseaux sociaux:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(data.fabricantWebAndSocial)}</DataValue>

                {data.fabricantWebAndSocialDetails && (
                  <>
                    <DataLabel>Détails web/social:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(data.fabricantWebAndSocialDetails)}</DataValue>
                  </>
                )}

                {data.fabricantPresentation && (
                  <>
                    <DataLabel>Présentation:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(data.fabricantPresentation)}</DataValue>
                  </>
                )}
              </DataGrid>
            </Section>
          )}

          {data.certificationAccepted && (
            <Section>
              <SectionTitle>Certification</SectionTitle>
              <DataValue>✓ Le candidat a certifié que les informations fournies sont exactes et véridiques.</DataValue>
            </Section>
          )}

          <Section>
            <SectionTitle>Pièces proposées ({data.pieces?.length || 0})</SectionTitle>
            
            {data.pieces?.map((piece, index) => (
              <PieceCard key={index}>
                <PieceTitle>Pièce {index + 1} :&nbsp;&nbsp;<strong style={{fontSize: '1.3rem', letterSpacing: '0.05rem'}}>{piece.name}</strong></PieceTitle>

                {/* First image miniature */}
                {piece.uploadedMedia && piece.uploadedMedia.length > 0 && (() => {
                  const firstImage = piece.uploadedMedia.find(media => media.mimetype.startsWith('image/'));
                  if (!firstImage) return null;

                  return (
                    <div style={{ marginBottom: '20px' }}>
                      <img
                        src={`${mediaBaseUrl}/${firstImage.path}`}
                        alt={firstImage.originalname}
                        style={{
                          width: '150px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleMediaClick(`${mediaBaseUrl}/${firstImage.path}`)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML = '<div style="font-size: 36px; color: #adb5bd;">📁</div>';
                        }}
                      />
                    </div>
                  );
                })()}

                <DataGrid>
                  <DataLabel>Typologie:</DataLabel>
                  <DataValue>
                    {piece.typologie === 'autre' && piece.typologieOther
                      ? makeUrlsAndEmailsLinks(piece.typologieOther)
                      : FDCategories.find(cat => cat.value === piece.typologie)?.label || piece.typologie || '-'}
                  </DataValue>

                  <DataLabel>Date de création:</DataLabel>
                  <DataValue>{makeUrlsAndEmailsLinks(piece.creationDate)}</DataValue>

                  <DataLabel>Savoir-faire:</DataLabel>
                  <DataValue>{makeUrlsAndEmailsLinks(piece.savoirFaire)}</DataValue>

                  <DataLabel>Durabilité:</DataLabel>
                  <DataValue>{makeUrlsAndEmailsLinks(piece.durability)}</DataValue>

                  <DataLabel>Présentation:</DataLabel>
                  <DataValue>{makeUrlsAndEmailsLinks(piece.presentationText)}</DataValue>

                  <DataLabel>Dimensions:</DataLabel>
                  <DataValue>{makeUrlsAndEmailsLinks(piece.dimensions)}</DataValue>

                  <DataLabel>Matériaux:</DataLabel>
                  <DataValue>{makeUrlsAndEmailsLinks(piece.materials)}</DataValue>

                  {piece.poids && (
                    <>
                      <DataLabel>Poids:</DataLabel>
                      <DataValue>{makeUrlsAndEmailsLinks(piece.poids)}</DataValue>
                    </>
                  )}
                </DataGrid>

                {/* Designer info for the piece */}
                {(data.userType === 'fabricant' && piece.designerCompanyName) && (
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #d0e8ff' }}>
                    <h5 style={{ color: '#0000FF', marginBottom: '12px', fontSize: '1rem' }}>Informations du Designer</h5>
                    <DataGrid>
                      <DataLabel>Nom de l'entreprise:</DataLabel>
                      <DataValue>{makeUrlsAndEmailsLinks(piece.designerCompanyName)}</DataValue>

                      {piece.designerInternationalPresence && (
                        <>
                          <DataLabel>Présence internationale:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.designerInternationalPresence)}</DataValue>
                        </>
                      )}

                      {piece.designerInternationalDetails && (
                        <>
                          <DataLabel>Détails international:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.designerInternationalDetails)}</DataValue>
                        </>
                      )}

                      {piece.designerWebAndSocial && (
                        <>
                          <DataLabel>Site web et réseaux sociaux:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.designerWebAndSocial)}</DataValue>
                        </>
                      )}

                      {piece.designerWebAndSocialDetails && (
                        <>
                          <DataLabel>Détails web/social:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.designerWebAndSocialDetails)}</DataValue>
                        </>
                      )}

                      {piece.designerPresentation && (
                        <>
                          <DataLabel>Présentation:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.designerPresentation)}</DataValue>
                        </>
                      )}
                    </DataGrid>
                  </div>
                )}

                {/* Fabricant info for the piece */}
                {(data.userType === 'designer' && piece.fabricantCompanyName) && (
                  <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #d0e8ff' }}>
                    <h5 style={{ color: '#0000FF', marginBottom: '12px', fontSize: '1rem' }}>Informations du Fabricant/Éditeur</h5>
                    <DataGrid>
                      <DataLabel>Nom de l'entreprise:</DataLabel>
                      <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantCompanyName)}</DataValue>

                      {piece.fabricantCodifab && (
                        <>
                          <DataLabel>Cotisant CODIFAB:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantCodifab)}</DataValue>
                        </>
                      )}

                      {piece.fabricantAmeublement && (
                        <>
                          <DataLabel>Adhérent Ameublement français:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantAmeublement)}</DataValue>
                        </>
                      )}

                      {piece.fabricantInternationalPresence && (
                        <>
                          <DataLabel>Présence internationale:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantInternationalPresence)}</DataValue>
                        </>
                      )}

                      {piece.fabricantInternationalDetails && (
                        <>
                          <DataLabel>Détails international:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantInternationalDetails)}</DataValue>
                        </>
                      )}

                      {piece.fabricantWebAndSocial && (
                        <>
                          <DataLabel>Site web et réseaux sociaux:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantWebAndSocial)}</DataValue>
                        </>
                      )}

                      {piece.fabricantWebAndSocialDetails && (
                        <>
                          <DataLabel>Détails web/social:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantWebAndSocialDetails)}</DataValue>
                        </>
                      )}

                      {piece.fabricantPresentation && (
                        <>
                          <DataLabel>Présentation:</DataLabel>
                          <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantPresentation)}</DataValue>
                        </>
                      )}
                    </DataGrid>
                  </div>
                )}

                {/* Show submitter info if they provided it at submission level but not piece level */}
                {(data.userType === 'designer' && !piece.fabricantCompanyName && data.fabricantCompanyName) && (
                  <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#fffbf0', borderRadius: '8px', border: '1px solid #ffeaa7' }}>
                    <p style={{ color: '#666', fontSize: '13px', fontStyle: 'italic', margin: 0 }}>
                      Partenaire fabricant par défaut: {makeUrlsAndEmailsLinks(data.fabricantCompanyName)}
                    </p>
                  </div>
                )}

                {(data.userType === 'fabricant' && !piece.designerCompanyName && data.designerCompanyName) && (
                  <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#fffbf0', borderRadius: '8px', border: '1px solid #ffeaa7' }}>
                    <p style={{ color: '#666', fontSize: '13px', fontStyle: 'italic', margin: 0 }}>
                      Partenaire designer par défaut: {makeUrlsAndEmailsLinks(data.designerCompanyName)}
                    </p>
                  </div>
                )}
                
                {piece.uploadedMedia && piece.uploadedMedia.length > 0 && (
                  <>
                    <h5 style={{ marginTop: '15px', marginBottom: '10px' }}>
                      Médias ({piece.uploadedMedia.length})
                    </h5>
                    <MediaGrid>
                      {piece.uploadedMedia.map((media, idx) => (
                        <MediaItem key={idx}>
                          <MediaThumb
                            onClick={() => handleMediaClick(`${mediaBaseUrl}/${media.path}`)}
                          >
                            {media.mimetype.startsWith('image/') ? (
                              <img
                                src={`${mediaBaseUrl}/${media.path}`}
                                alt={media.originalname}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentNode.innerHTML = '<div style="font-size: 36px; color: #adb5bd;">📁</div>';
                                }}
                              />
                            ) : (
                              <MediaIcon>
                                {media.mimetype.startsWith('video/') ? '🎥' : '📄'}
                              </MediaIcon>
                            )}
                          </MediaThumb>
                          <MediaInfo>
                            <MediaName title={media.originalname}>
                              {media.originalname}
                            </MediaName>
                            {media.credit && (
                              <MediaCredit>© {media.credit}</MediaCredit>
                            )}
                          </MediaInfo>
                        </MediaItem>
                      ))}
                    </MediaGrid>
                  </>
                )}
              </PieceCard>
            ))}
          </Section>
        </ContentSection>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default CandidatureCompletePopup;